import { JobDtoType } from '../domain/jobs';
import { IAttendeeOrganizationDTO } from './attendees.dao';
import {
    IEventRequestParams,
    IImageWithObjectKey,
    IImageWithUrl,
    IPaginationParams,
    ISortFieldsRequestParams,
    ISortRequestParams,
    IWithExternalIdEventRequestParams,
    SortOrder
} from './shared.dto';
import { IConnectionDetailsDTO } from './connections.dao';
import { TGetArtifactsSourcesRequestParams } from './artifacts.dao';
import { ConnectionVersion } from '../constants/common.constants';

abstract class IMeetingsDAO {
    public abstract getEventMeetings(params: IGetMeetingsRequestParams, signal?: AbortSignal): Promise<IEventMeetingsDTO>;
    public abstract getMeetingsCSV(params: IEventRequestParams);
    public abstract getRegistrantReportCSV(params: IEventRequestParams);
    public abstract getMeetingDetails(params: IMeetingRequestParams): Promise<IMeetingDetailsDTO>;
    public abstract updateMeetingDetails(meetingDetails: IMeetingDetailsDTO, params: IMeetingRequestParams);
    public abstract importMeetings(data: IImportMeetingsRequestParams & IEventRequestParams): Promise<JobDtoType>;
    public abstract createMeeting(dateTimeList: IMeetingSaveDTO[], params: IEventRequestParams): Promise<void>;
    public abstract assignRooms(params: IEventRequestParams): Promise<JobDtoType>;
    public abstract deleteMeetingInEvent(params: IMeetingRequestParams): Promise<JobDtoType>;
    public abstract getMeetingDetailsByExternalId(params: IWithExternalIdEventRequestParams): Promise<IMeetingDetailsDTO>;
    public abstract getMeetingsStartDateTimes(params: IEventRequestParams): Promise<IStartDateTimesDTO>;
    public abstract getScheduledMeetingsStartDateTimes(params: IEventRequestParams): Promise<IStartDateTimesDTO>;
    public abstract cancelMeetingsByStartDateTimes(params: IEventRequestParams & IStartDateTimesDTO): Promise<JobDtoType>;
    public abstract removeMeetingsByStartDateTimes(params: IEventRequestParams & IStartDateTimesDTO): Promise<JobDtoType>;
    public abstract updateMeetingAttendees(operations: IUpdateMeetingAttendees, params: IMeetingRequestParams): Promise<IMeetingDetailsDTO>;
    public abstract deleteLink(linkId: number, params: IMeetingRequestParams): Promise<void>;
    public abstract getEventArtifactsSources(params: TGetArtifactsSourcesRequestParams): Promise<IEventMeetingsDTO>;
}

enum MeetingStatus {
    Cancelled = 'Cancelled',
    Scheduled = 'Scheduled',
    Removed = 'Removed',
}

enum MeetingStatusFilter {
    CANCELLED = 'CANCELLED',
    SCHEDULED = 'SCHEDULED',
    REMOVED = 'REMOVED'
}

enum Access {
    Closed = 'Closed',
    Open = 'Open',
    Plenary = 'Plenary',
    Bookable = 'Bookable'
}

enum RecordingsStatus {
    No = 'No',
    Yes = 'Yes'
}

enum AttendeesAction {
    Add = 'ADD',
    Remove = 'REMOVE'
}

enum AttendeesUpdateRole {
    presenters= 'Presenter',
    participants= 'Participant',
    hosts = 'Host'
}

type IGetMeetingsRequestParams =
    IIgnoreRegistrantsRequest & IEventRequestParams &
    (ISortFieldsRequestParams<TGetMeetingsSortKeys> | ISortRequestParams<TGetMeetingsSortKeys>) &
    IMeetingFilterParams &
    IPaginationParams;

interface IMeetingStartDateTimeDetailDTO {
    dateTime: string;
    calendarCancellationCount: number;
}

interface IStartDateTimesDTO {
    dates: string[];
    dateDetails?: IMeetingStartDateTimeDetailDTO[];
}

interface IMeetingFilterParams extends IEventRequestParams {
    withoutRoom?: boolean;
    meetingStartHour?: string;
    types?: string;
    connectionTypes?: string;
    status?: MeetingStatusFilter;
    reportType?: string;
}

interface IAssignRoomRequestParams {
    eventId: number;
    accountId: number;
    connectionVersion: ConnectionVersion;
}

interface IMeetingSearchParams extends IEventRequestParams {
    search?: string;
}

interface IMeetingRequestParams extends IEventRequestParams {
    meetingId: number;
}

interface IGroupingStrategiesDTO {
    groupingStrategies: IGroupingStrategyDetailsDTO[];
}

interface IGroupingStrategyDetailsDTO {
    name: string;
    label: string;
    order: number;
}

interface IEventMeetingsDTO {
    content: IMeetingDetailsDTO[];
    totalElements: number;
}

interface IMeetingRoomDTO {
    roomId: number;
    roomName: string;
    roomLabel: string;
    operators?: [];
}

interface IMeetingDetailsDTO {
    externalId: string;
    meetingId: number;
    room: IMeetingRoomDTO;
    topic: string;
    track: string;
    status: MeetingStatus;
    defaultMeetingDuration?: number;
    startDateTime: Date;
    endDateTime: Date;
    hosts: IAttendeeOrganizationDTO[];
    presenters: IAttendeeOrganizationDTO[];
    participants: IAttendeeOrganizationDTO[];
    registrants: IAttendeeOrganizationDTO[];
    supports: IAttendeeOrganizationDTO[];
    notes: string;
    type: string;
    path: string;
    passcode: string;
    meetingAccess: Access;
    meetingRecordEnabled?: boolean;
    featured: boolean;
    replayEnabled: boolean;
    audienceCapacityEnabled?: boolean;
    audienceCapacity?: number | null;
    inviteAdditionalAttendeesEnabled?: boolean;
    presenterPortalLink?: string | null;
    connections?: IConnectionDetailsDTO[];
    addedLinks: ILinkDTO[];
    blueprintId: string | null;
    expiryDays: number | null;
    goLiveDateTime: Date | null;
}

interface IMeetingCoPilotDetailsDTO {
    meetingId: number;
    topic: string;
    startDateTime: Date;
    endDateTime: Date;
    hosts: IAttendeeOrganizationDTO[];
    presenters: IAttendeeOrganizationDTO[];
    participants: IAttendeeOrganizationDTO[];
    type: string;
    status: MeetingStatus;
}

type TGetMeetingsSortKeys = 'startDateTime' | 'endDateTime' | 'status' | 'organizations.role' | 'organizations.organization.name';
interface IMeetingSaveDTO {
    externalId: string;
    room: {
        roomId: number;
        roomName: string;
        roomLabel: string;
    };
    topic: string;
    status: MeetingStatus;
    hosts: IAttendeeOrganizationDTO[];
    presenters: IAttendeeOrganizationDTO[];
    defaultMeetingDuration?: number;
    startDateTime: Date;
    endDateTime: Date;
    participants: IAttendeeOrganizationDTO[];
    registrants: IAttendeeOrganizationDTO[];
    supports: IAttendeeOrganizationDTO[];
    notes: string;
    type: string;
    track: string;
    path: string;
    passcode: string;
    meetingLink?: string;
    meetingLinkWithEmbeddedBypassCode?: string;
    meetingAccess: Access;
    meetingRecordEnabled?: boolean;
    thumbnailImage?: IImageWithObjectKey;
    thumbnailImageWithUrl?: IImageWithUrl;
    heroImage?: IImageWithObjectKey;
    heroImageWithUrl?: IImageWithUrl;
    featured: boolean;
    replayEnabled: boolean;
    audienceCapacityEnabled?: boolean;
    audienceCapacity?: number | null;
    inviteAdditionalAttendeesEnabled?: boolean;
    presenterPortalLink?: string;
    presenterPortalLinkEnabled?: boolean;
    links?: ILinkDTO[];
    connections?: IConnectionDetailsDTO[];
    blueprintId: string | null;
    connectionVersion?: ConnectionVersion;
    expiryDays: number | null;
    goLiveDateTime?: Date | null;
}

interface IImportMeetingsRequestParams {
    file: File;
    timezone: string;
    defaultDuration: string;
    separator: string;
    groupingStrategy: string;
    dateFormatType: string;
    connectionVersion: ConnectionVersion;
}

interface IMeetingAttendees {
    organizationId: number;
    attendeeId?: number;
    action: AttendeesAction;
    role: AttendeesUpdateRole;
}

interface IUpdateMeetingAttendees {
    operations: IMeetingAttendees[];
}

interface IAddedLinks {
    addedLinks: ILinkDTO[];
}
interface ILinkDTO {
    linkId: number;
    label?: string;
    type: string;
    url: string;
}

type TSelectedFilterHours = {
    filterName: 'selectedFilterHours';
    filterValues: Date[];
};

type TSavedSelectedFilterHours = {
    filterName: 'selectedFilterHours';
    filterValues: string[];
};

type TSelectedFilterTypes = {
    filterName: 'selectedFilterTypes';
    filterValues: string;
};

type TSelectedWithoutRoom = {
    filterName: 'withoutRoom';
    filterValues: boolean;
};

type TSelectedSortOrder = {
    filterName: 'selectedSortOrder';
    filterValues: SortOrder;
};

type TSelectedFilterStatuses = {
    filterName: 'selectedStatuses';
    filterValues: string;
};

type TSelectedFilterChaperone = {
    filterName: 'selectedChaperone';
    filterValues: string;
};

type TSelectedFilterOperators = {
    filterName: 'selectedFilterOperators';
    filterValues: string;
};

type TMeetingsFilterItem = TSavedSelectedFilterHours
    | TSelectedFilterHours
    | TSelectedFilterTypes
    | TSelectedWithoutRoom
    | TSelectedSortOrder
    | TSelectedFilterStatuses
    | TSelectedFilterChaperone
    | TSelectedFilterOperators
;

type TSavedMeetingsFilterItem = Record<string, TMeetingsFilterItem[]>;

interface IIgnoreRegistrantsRequest {
    ignoreRegistrants: boolean;
}

export {
    IEventMeetingsDTO,
    IMeetingDetailsDTO,
    IMeetingCoPilotDetailsDTO,
    IMeetingsDAO,
    IGroupingStrategiesDTO,
    IGroupingStrategyDetailsDTO,
    IMeetingSaveDTO,
    IStartDateTimesDTO,
    IMeetingStartDateTimeDetailDTO,
    TGetMeetingsSortKeys,
    IMeetingRequestParams,
    IImportMeetingsRequestParams,
    IAssignRoomRequestParams,
    IMeetingSearchParams,
    MeetingStatus,
    Access,
    RecordingsStatus,
    IMeetingRoomDTO,
    IMeetingFilterParams,
    IGetMeetingsRequestParams,
    IMeetingAttendees,
    AttendeesAction,
    IUpdateMeetingAttendees,
    AttendeesUpdateRole,
    MeetingStatusFilter,
    ILinkDTO,
    IAddedLinks,
    TSavedMeetingsFilterItem,
    TMeetingsFilterItem,
    TSavedSelectedFilterHours,
    TSelectedFilterTypes,
    TSelectedFilterStatuses,
    TSelectedFilterChaperone,
    TSelectedFilterOperators,
};
